// Phase 9A.2 / 9A.3 — PublicSignalCardItem.
//
// Renders a single curated public-ready signal card from the Phase 9A.1
// `usePublicSignalCards` hook. Pure presentation — no fetch, no state,
// no side effects. Surfaces ONLY safe public fields:
//   id, fixture_id, canonical_code, signal_label, value_label,
//   risk_label, confidence_label, fair/market probability + odds,
//   edge_probability, value_edge, candidate_rank, data_tier, summary,
//   caution_flags, generated_at, expires_at.
//
// Sensitive columns are not in the view-model surface (the 9A.1 grant
// model excludes them server-side). This component therefore cannot
// leak `explanation`, `input_snapshot`, evidence_payload, raw_payload,
// grade evidence, ledger evidence, closing-line evidence, or provider
// evidence.
//
// 9A.3: optional `onInspect` callback opens the curated detail drawer
// owned by PublicSignalCardSection. When undefined, no Inspect button
// renders (component remains pure-presentation, drop-in replaceable).
//
// Doctrine copy:
//   - "Manual placement only · No bookmaker connection"
//   - No best-bet / sure-win / guaranteed / lock / banker / ROI /
//     profit / staking / bankroll / available CLV / public track
//     record vocabulary anywhere on the card.

import type { CSSProperties } from "react";

import type { PublicSignalCardViewModel } from "./usePublicSignalCards.ts";
import { fmtNum, fmtPct, fmtPctSigned } from "./signal-format.ts";
import {
  formatCandidateRank,
  formatDataTierCopy,
  formatGeneratedAt,
  formatTier,
  ratioToPct,
  watchlistSaveLabel,
} from "./public-signal-format.ts";
import * as s from "./public-signal-card.styles.ts";

export interface PublicSignalCardItemProps {
  card: PublicSignalCardViewModel;
  onInspect?: (card: PublicSignalCardViewModel) => void;
  // Phase 9A.4 — optional save/unsave wiring. When undefined, no save
  // button renders.
  isSaved?: boolean;
  saveBusy?: boolean;
  onToggleSave?: (card: PublicSignalCardViewModel) => void;
}

const TRUTH_FOOTER =
  "Prepared intelligence. Probability view only. Manual placement only. No bookmaker connection. No automation.";

function edgeStyle(pct: number | null): CSSProperties {
  if (pct === null || !Number.isFinite(pct)) return s.metaValue;
  if (pct > 0) return s.metaValueEdgePositive;
  return s.metaValueEdgeNegative;
}

export function PublicSignalCardItem(props: PublicSignalCardItemProps): JSX.Element {
  const { card, onInspect, isSaved, saveBusy, onToggleSave } = props;
  const saveEnabled = onToggleSave !== undefined;
  const savedNow = isSaved === true;
  const busyNow = saveBusy === true;
  const fairPct = ratioToPct(card.fair_probability);
  const impliedPct = ratioToPct(card.market_implied_probability);
  const edgePct = ratioToPct(card.edge_probability);

  const subtitleParts: string[] = [card.canonical_code];
  if (card.value_label !== null) subtitleParts.push(card.value_label);
  if (card.candidate_rank !== null) {
    subtitleParts.push(formatCandidateRank(card.candidate_rank));
  }
  const subtitle = subtitleParts.join(" · ");

  return (
    <article style={s.card} aria-label={`Curated signal card — ${card.canonical_code}`}>
      <div style={s.cardHeaderRow}>
        <div style={s.cardHeaderLeft}>
          <span style={s.modulePill}>Public-ready</span>
          {card.risk_label !== null ? (
            <span style={s.labelPill}>{`Risk · ${card.risk_label}`}</span>
          ) : null}
          {card.confidence_label !== null ? (
            <span style={s.labelPill}>{`Confidence · ${card.confidence_label}`}</span>
          ) : null}
        </div>
        <span style={s.labelPill}>{formatTier(card.data_tier)}</span>
      </div>

      <h3 style={s.cardTitle}>{card.signal_label ?? "Curated signal"}</h3>
      <span style={s.cardSubtitle}>{subtitle}</span>
      {card.summary !== null && card.summary.length > 0 ? (
        <p style={s.cardSummary}>{card.summary}</p>
      ) : null}

      <div style={s.metaGrid}>
        <span style={s.metaKey}>Fair probability</span>
        <span style={s.metaValue}>{fmtPct(fairPct, 1)}</span>

        <span style={s.metaKey}>Market implied</span>
        <span style={s.metaValue}>{fmtPct(impliedPct, 1)}</span>

        <span style={s.metaKey}>Fair odds</span>
        <span style={s.metaValue}>{fmtNum(card.fair_odds_decimal, 2)}</span>

        <span style={s.metaKey}>Market odds</span>
        <span style={s.metaValue}>{fmtNum(card.market_odds_decimal, 2)}</span>

        <span style={s.metaKey}>Edge (probability)</span>
        <span style={edgeStyle(edgePct)}>{fmtPctSigned(edgePct, 1)}</span>

        <span style={s.metaKey}>Value edge</span>
        <span style={edgeStyle(card.value_edge)}>{fmtNum(card.value_edge, 4)}</span>
      </div>

      {card.caution_flags.length > 0 ? (
        <div style={s.cautionRow}>
          {card.caution_flags.map((flag) => (
            <span key={flag} style={s.cautionFlag}>
              {flag}
            </span>
          ))}
        </div>
      ) : null}

      <span style={s.generatedLine}>{formatGeneratedAt(card.generated_at)}</span>
      <span style={s.truthFooter}>{TRUTH_FOOTER}</span>
      <span style={s.cardSubtitle}>{formatDataTierCopy(card.data_tier)}</span>
      {saveEnabled ? (
        <button
          type="button"
          onClick={() => onToggleSave!(card)}
          disabled={busyNow}
          style={savedNow ? s.savedButton : s.saveButton}
          aria-pressed={savedNow}
          aria-label={savedNow ? "Remove from watchlist" : "Save signal to watchlist"}
        >
          {watchlistSaveLabel(savedNow, busyNow)}
        </button>
      ) : null}
      {onInspect !== undefined ? (
        <button
          type="button"
          onClick={() => onInspect(card)}
          style={s.inspectButton}
          aria-label="Inspect curated signal card detail"
        >
          Inspect
        </button>
      ) : null}
    </article>
  );
}
